/**
 * CoiDrawMap: the community-of-interest map — the right-docked counterpart
 * to the district-drawing MapView.
 *
 * Always visible: the jurisdiction with the street basemap and faint block
 * outlines for reference. Two boundary sources feed it:
 *  - the hand-drawn polygon (drawEnabled), and
 *  - an AI-sketch candidate, shown as a lighter dashed preview until the
 *    resident attaches it.
 * An attached boundary renders as a solid fill regardless of which mode
 * produced it.
 *
 * Drawing (drawEnabled):
 *  - Click mode: click to drop points. A live "rubber band" shows the next
 *    edge and the closing edge, with a translucent fill preview. Close the
 *    shape by clicking the first point, double-clicking, pressing Enter, or
 *    "Finish". Backspace removes the last point; Esc clears.
 *  - Trace mode: press and drag to trace the outline freehand; releasing
 *    closes the shape (points are thinned so the polygon stays light).
 *  - Editing: once attached, drag any vertex to adjust it, click a midpoint
 *    handle to add a vertex, and right-click (or Alt-click) a vertex to
 *    delete it. Every edit re-attaches the updated polygon.
 *  - A self-crossing outline is flagged before submission, and the area is
 *    shown so people can sanity-check the scale of what they drew.
 */
import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { TenantConfig } from '../../lib/api';
import { registerPmtilesProtocol, resolveBlocksTilesUrl } from '../../lib/pmtiles';
import { INITIAL_MIN_ZOOM } from '../../map/MapShell';
import { ensureBasemap } from '../../map/basemap';
import { GLYPHS_URL, registerLocalGlyphs } from '../../map/glyphs';
import { useBasemapStore } from '../../stores/basemap';
import { COI_BOUNDARY_COLOR } from './CoiBoundaryMap';
import { AlertIcon, PenIcon, PolygonIcon, UndoIcon } from '../icons';

const DRAW_SOURCE_ID = 'coi-draw';
const DRAW_FILL_LAYER_ID = 'coi-draw-fill';
const DRAW_LINE_LAYER_ID = 'coi-draw-line';
const DRAW_GUIDE_LAYER_ID = 'coi-draw-guide';
const VERTICES_LAYER_ID = 'coi-draw-vertices';
const ACCEPTED_SOURCE_ID = 'coi-accepted';
const ACCEPTED_FILL_LAYER_ID = 'coi-accepted-fill';
const ACCEPTED_LINE_LAYER_ID = 'coi-accepted-line';
const HANDLES_SOURCE_ID = 'coi-handles';
const HANDLES_LAYER_ID = 'coi-handles-vertices';
const MIDPOINTS_LAYER_ID = 'coi-handles-midpoints';
const SKETCH_SOURCE_ID = 'coi-sketch';
const SKETCH_FILL_LAYER_ID = 'coi-sketch-fill';
const SKETCH_LINE_LAYER_ID = 'coi-sketch-line';
const BLOCKS_REF_SOURCE_ID = 'coi-blocks-ref';
const BLOCKS_REF_LAYER_ID = 'coi-blocks-ref-lines';

/** Pixel radius for "close the shape on the first point" and click dedupe. */
const SNAP_PX = 10;

type Pt = [number, number];
type DrawStyle = 'click' | 'trace';

interface CoiDrawMapProps {
  tenant: TenantConfig;
  /** True when drawing is active (boundary mode "draw"). */
  drawEnabled: boolean;
  /** The attached boundary — solid fill, shown whatever mode made it. */
  accepted: Record<string, unknown> | null;
  /** An unaccepted AI-sketch candidate — lighter dashed preview. */
  preview: Record<string, unknown> | null;
  /** Called with the finished (or edited) GeoJSON Polygon. */
  onAccept: (polygon: Record<string, unknown>) => void;
  /** Called when the user clears the drawing. */
  onClear: () => void;
  /** Ask the parent to switch to draw mode (the on-map "Draw" shortcut). */
  onRequestDraw?: () => void;
  /** Called with the live map once the style has loaded. */
  onMapReady?: (map: maplibregl.Map) => void;
}

function emptyCollection(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function polygonCollection(polygon: Record<string, unknown> | null): GeoJSON.FeatureCollection {
  if (!polygon) return emptyCollection();
  return {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', properties: {}, geometry: polygon as unknown as GeoJSON.Geometry }],
  };
}

/** The open ring (no repeated closing point) of a single-ring Polygon, or null. */
function editableRing(polygon: Record<string, unknown> | null): Pt[] | null {
  if (!polygon || polygon.type !== 'Polygon') return null;
  const rings = polygon.coordinates as Pt[][] | undefined;
  const ring = rings?.[0];
  if (!ring || ring.length < 4) return null;
  const open = ring.slice(0, -1).map((c) => [c[0], c[1]] as Pt);
  return open.length >= 3 ? open : null;
}

function closedPolygon(ring: Pt[]): GeoJSON.Polygon {
  const first = ring[0] as Pt;
  return { type: 'Polygon', coordinates: [[...ring, first]] };
}

/** Approximate geodesic area of a lng/lat ring in square meters. */
function ringAreaM2(ring: Pt[]): number {
  if (ring.length < 3) return 0;
  const R = 6378137;
  const rad = Math.PI / 180;
  let total = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const [lon1, lat1] = ring[i] as Pt;
    const [lon2, lat2] = ring[(i + 1) % ring.length] as Pt;
    total += (lon2 - lon1) * rad * (2 + Math.sin(lat1 * rad) + Math.sin(lat2 * rad));
  }
  return Math.abs((total * R * R) / 2);
}

function polygonAreaM2(polygon: Record<string, unknown> | null): number {
  if (!polygon) return 0;
  if (polygon.type === 'Polygon') {
    const rings = polygon.coordinates as Pt[][];
    return rings.reduce((sum, ring, i) => sum + (i === 0 ? 1 : -1) * ringAreaM2(ring.slice(0, -1)), 0);
  }
  if (polygon.type === 'MultiPolygon') {
    return (polygon.coordinates as Pt[][][]).reduce(
      (sum, poly) => sum + polygonAreaM2({ type: 'Polygon', coordinates: poly }),
      0,
    );
  }
  return 0;
}

function formatArea(m2: number): string {
  const sqmi = m2 / 2_589_988.11;
  if (sqmi >= 0.1) return `${sqmi.toFixed(sqmi >= 10 ? 0 : 2)} sq mi`;
  const acres = m2 / 4046.86;
  return `${Math.max(1, Math.round(acres)).toLocaleString('en-US')} acres`;
}

/** True when any two non-adjacent edges of the closed ring cross. */
function selfIntersects(ring: Pt[]): boolean {
  const n = ring.length;
  if (n < 4) return false;
  const cross = (a: Pt, b: Pt, c: Pt): number => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  for (let i = 0; i < n; i += 1) {
    const a1 = ring[i] as Pt;
    const a2 = ring[(i + 1) % n] as Pt;
    for (let j = i + 2; j < n; j += 1) {
      if (i === 0 && j === n - 1) continue; // edges sharing the closing vertex
      const b1 = ring[j] as Pt;
      const b2 = ring[(j + 1) % n] as Pt;
      const d1 = cross(b1, b2, a1);
      const d2 = cross(b1, b2, a2);
      const d3 = cross(a1, a2, b1);
      const d4 = cross(a1, a2, b2);
      if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
        return true;
      }
    }
  }
  return false;
}

export function CoiDrawMap({
  tenant,
  drawEnabled,
  accepted,
  preview,
  onAccept,
  onClear,
  onRequestDraw,
  onMapReady,
}: CoiDrawMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const verticesRef = useRef<Pt[]>([]);
  const cursorRef = useRef<Pt | null>(null);
  const tracingRef = useRef(false);
  const dragIndexRef = useRef<number | null>(null);
  const [vertexCount, setVertexCount] = useState(0);
  const [drawStyle, setDrawStyle] = useState<DrawStyle>('click');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nearFirst, setNearFirst] = useState(false);

  const onAcceptRef = useRef(onAccept);
  onAcceptRef.current = onAccept;
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;
  const onMapReadyRef = useRef(onMapReady);
  onMapReadyRef.current = onMapReady;
  const drawEnabledRef = useRef(drawEnabled);
  drawEnabledRef.current = drawEnabled;
  const drawStyleRef = useRef(drawStyle);
  drawStyleRef.current = drawStyle;
  const acceptedRef = useRef(accepted);
  acceptedRef.current = accepted;
  const previewRef = useRef(preview);
  previewRef.current = preview;

  const acceptedRing = editableRing(accepted);
  const crossing = acceptedRing ? selfIntersects(acceptedRing) : false;
  const area = polygonAreaM2(accepted);

  function setSource(id: string, data: GeoJSON.FeatureCollection): void {
    const map = mapRef.current;
    const source = map?.getSource<maplibregl.GeoJSONSource>(id);
    source?.setData(data);
  }

  /** In-progress sketch: vertices, the drawn path, the rubber band, a fill preview. */
  function refreshDrawLayers(): void {
    const vertices = verticesRef.current;
    const cursor = cursorRef.current;
    const features: GeoJSON.Feature[] = vertices.map((v, i) => ({
      type: 'Feature',
      properties: { index: i, first: i === 0 },
      geometry: { type: 'Point', coordinates: v },
    }));
    if (vertices.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { role: 'path' },
        geometry: { type: 'LineString', coordinates: vertices },
      });
    }
    const last = vertices[vertices.length - 1];
    const first = vertices[0];
    if (cursor && last && first && !tracingRef.current) {
      const guide: Pt[] = vertices.length >= 2 ? [last, cursor, first] : [last, cursor];
      features.push({
        type: 'Feature',
        properties: { role: 'guide' },
        geometry: { type: 'LineString', coordinates: guide },
      });
    }
    const ringPts = cursor && !tracingRef.current ? [...vertices, cursor] : vertices;
    if (ringPts.length >= 3) {
      features.push({ type: 'Feature', properties: {}, geometry: closedPolygon(ringPts) });
    }
    setSource(DRAW_SOURCE_ID, { type: 'FeatureCollection', features });
  }

  /** Vertex + midpoint handles for editing the attached polygon. */
  function refreshHandles(): void {
    const ring = drawEnabledRef.current ? editableRing(acceptedRef.current) : null;
    if (!ring || verticesRef.current.length > 0) {
      setSource(HANDLES_SOURCE_ID, emptyCollection());
      return;
    }
    const features: GeoJSON.Feature[] = [];
    ring.forEach((v, i) => {
      const next = ring[(i + 1) % ring.length] as Pt;
      features.push({ type: 'Feature', properties: { kind: 'vertex', index: i }, geometry: { type: 'Point', coordinates: v } });
      features.push({
        type: 'Feature',
        properties: { kind: 'mid', index: i },
        geometry: { type: 'Point', coordinates: [(v[0] + next[0]) / 2, (v[1] + next[1]) / 2] },
      });
    });
    setSource(HANDLES_SOURCE_ID, { type: 'FeatureCollection', features });
  }

  function resetSketch(): void {
    verticesRef.current = [];
    cursorRef.current = null;
    tracingRef.current = false;
    setVertexCount(0);
    setNearFirst(false);
    refreshDrawLayers();
  }

  function clearDrawing(): void {
    resetSketch();
    onClearRef.current();
  }

  function finishDrawing(): void {
    const vertices = verticesRef.current;
    if (vertices.length < 3) return;
    const polygon = closedPolygon(vertices);
    resetSketch();
    onAcceptRef.current(polygon as unknown as Record<string, unknown>);
  }

  function undoPoint(): void {
    if (verticesRef.current.length === 0) return;
    verticesRef.current = verticesRef.current.slice(0, -1);
    setVertexCount(verticesRef.current.length);
    refreshDrawLayers();
  }

  // Keep the attached / sketch layers in sync with props. These no-op until
  // the style has loaded; the load handler applies the current ref values.
  useEffect(() => {
    setSource(ACCEPTED_SOURCE_ID, polygonCollection(accepted));
    refreshHandles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accepted]);
  useEffect(() => {
    setSource(SKETCH_SOURCE_ID, polygonCollection(preview));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  // Turning the draw tool off discards any unfinished sketch; the cursor
  // and pan behavior follow the tool.
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      map.getCanvas().style.cursor = drawEnabled ? 'crosshair' : '';
      if (drawEnabled && drawStyle === 'trace') map.dragPan.disable();
      else map.dragPan.enable();
    }
    if (!drawEnabled) resetSketch();
    refreshHandles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawEnabled, drawStyle]);

  // Keyboard: Enter finishes, Backspace removes the last point, Esc clears.
  useEffect(() => {
    if (!drawEnabled) return;
    function onKeyDown(event: KeyboardEvent): void {
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (verticesRef.current.length === 0) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        finishDrawing();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        undoPoint();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        resetSketch();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawEnabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let tilesUrl: string;
    try {
      registerPmtilesProtocol();
      tilesUrl = resolveBlocksTilesUrl({
        tilesUrl: tenant.map.tiles_url,
        datasetHash: tenant.map.dataset_hash,
      });
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Tile source misconfigured.');
      return;
    }

    registerLocalGlyphs();
    const map = new maplibregl.Map({
      container,
      style: {
        version: 8,
        name: `${tenant.slug}-coi`,
        glyphs: GLYPHS_URL,
        sources: {
          [BLOCKS_REF_SOURCE_ID]: {
            type: 'vector',
            url: `pmtiles://${tilesUrl}`,
            promoteId: { [tenant.map.source_layer]: tenant.map.id_property },
          },
        },
        layers: [
          {
            id: BLOCKS_REF_LAYER_ID,
            type: 'line',
            source: BLOCKS_REF_SOURCE_ID,
            'source-layer': tenant.map.source_layer,
            paint: {
              'line-color': 'rgba(68, 64, 60, 0.22)',
              'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.45, 14, 0.9],
            },
          },
        ],
      },
      ...(tenant.map.bounds ? { bounds: tenant.map.bounds, fitBoundsOptions: { padding: 16 } } : {}),
      ...(tenant.map.center
        ? { center: tenant.map.center, zoom: INITIAL_MIN_ZOOM }
        : { center: [-122.27, 37.8], zoom: INITIAL_MIN_ZOOM }),
      doubleClickZoom: false,
      attributionControl: { compact: true },
    });
    if (map.getZoom() < INITIAL_MIN_ZOOM) {
      map.setZoom(INITIAL_MIN_ZOOM);
    }
    mapRef.current = map;
    if (import.meta.env.DEV) (window as unknown as { __rdMap?: maplibregl.Map }).__rdMap = map;
    map.getCanvas().style.cursor = drawEnabledRef.current ? 'crosshair' : '';

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 100, unit: 'imperial' }), 'bottom-right');

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => map.resize()) : null;
    resizeObserver?.observe(container);

    let styleReady = false;
    const unsubscribeBasemap = useBasemapStore.subscribe((state) => {
      const live = mapRef.current;
      if (live && styleReady) ensureBasemap(live, state.choice);
    });

    const toPt = (lngLat: maplibregl.LngLat): Pt => [lngLat.lng, lngLat.lat];
    const pxDist = (a: Pt, b: Pt): number => {
      const pa = map.project(a);
      const pb = map.project(b);
      return Math.hypot(pa.x - pb.x, pa.y - pb.y);
    };

    /* ── Click-to-place drawing ─────────────────────────────────── */
    map.on('click', (event) => {
      if (!drawEnabledRef.current || drawStyleRef.current !== 'click') return;
      // Clicks on edit handles belong to the editor.
      if (verticesRef.current.length === 0 && editableRing(acceptedRef.current)) return;
      const pt = toPt(event.lngLat);
      const vertices = verticesRef.current;
      const first = vertices[0];
      const last = vertices[vertices.length - 1];
      // Clicking the first point closes the shape.
      if (first && vertices.length >= 3 && pxDist(pt, first) <= SNAP_PX) {
        finishDrawing();
        return;
      }
      // A second click on the same spot is the tail of a double-click: finish.
      if (last && pxDist(pt, last) <= 4) {
        if (vertices.length >= 3) finishDrawing();
        return;
      }
      verticesRef.current = [...vertices, pt];
      setVertexCount(verticesRef.current.length);
      refreshDrawLayers();
    });
    map.on('dblclick', (event) => event.preventDefault());

    map.on('mousemove', (event) => {
      const pt = toPt(event.lngLat);
      // Vertex drag in progress.
      if (dragIndexRef.current !== null) {
        const ring = editableRing(acceptedRef.current);
        if (!ring) return;
        ring[dragIndexRef.current] = pt;
        const polygon = closedPolygon(ring);
        setSource(ACCEPTED_SOURCE_ID, polygonCollection(polygon as unknown as Record<string, unknown>));
        acceptedRef.current = polygon as unknown as Record<string, unknown>;
        refreshHandles();
        return;
      }
      if (!drawEnabledRef.current) return;
      if (tracingRef.current) {
        const last = verticesRef.current[verticesRef.current.length - 1];
        if (!last || pxDist(pt, last) >= 6) {
          verticesRef.current = [...verticesRef.current, pt];
          setVertexCount(verticesRef.current.length);
          refreshDrawLayers();
        }
        return;
      }
      if (verticesRef.current.length > 0) {
        cursorRef.current = pt;
        const first = verticesRef.current[0];
        const near = !!first && verticesRef.current.length >= 3 && pxDist(pt, first) <= SNAP_PX;
        setNearFirst(near);
        map.getCanvas().style.cursor = near ? 'pointer' : 'crosshair';
        refreshDrawLayers();
      }
    });
    map.getCanvas().addEventListener('mouseleave', () => {
      cursorRef.current = null;
      refreshDrawLayers();
    });

    /* ── Freehand tracing ───────────────────────────────────────── */
    map.on('mousedown', (event) => {
      if (!drawEnabledRef.current || drawStyleRef.current !== 'trace') return;
      if (event.originalEvent.button !== 0) return;
      if (editableRing(acceptedRef.current)) return; // edit handles own the drag
      event.preventDefault();
      tracingRef.current = true;
      verticesRef.current = [toPt(event.lngLat)];
      setVertexCount(1);
      refreshDrawLayers();
    });
    const endTrace = (): void => {
      if (!tracingRef.current) return;
      tracingRef.current = false;
      if (verticesRef.current.length >= 3) finishDrawing();
      else resetSketch();
    };
    map.on('mouseup', endTrace);

    /* ── Editing the attached polygon ───────────────────────────── */
    map.on('mousedown', HANDLES_LAYER_ID, (event) => {
      if (!drawEnabledRef.current) return;
      const feature = event.features?.[0];
      const index = feature?.properties?.index as number | undefined;
      if (index === undefined) return;
      const ring = editableRing(acceptedRef.current);
      // Right-click or Alt-click deletes a vertex (a triangle is the minimum).
      if (event.originalEvent.button === 2 || event.originalEvent.altKey) {
        event.preventDefault();
        if (ring && ring.length > 3) {
          ring.splice(index, 1);
          onAcceptRef.current(closedPolygon(ring) as unknown as Record<string, unknown>);
        }
        return;
      }
      if (event.originalEvent.button !== 0) return;
      event.preventDefault();
      dragIndexRef.current = index;
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
    });
    map.on('contextmenu', HANDLES_LAYER_ID, (event) => event.preventDefault());
    map.on('mouseup', () => {
      if (dragIndexRef.current === null) return;
      dragIndexRef.current = null;
      if (!(drawEnabledRef.current && drawStyleRef.current === 'trace')) map.dragPan.enable();
      map.getCanvas().style.cursor = drawEnabledRef.current ? 'crosshair' : '';
      const ring = editableRing(acceptedRef.current);
      if (ring) onAcceptRef.current(closedPolygon(ring) as unknown as Record<string, unknown>);
    });
    map.on('click', MIDPOINTS_LAYER_ID, (event) => {
      if (!drawEnabledRef.current) return;
      const index = event.features?.[0]?.properties?.index as number | undefined;
      const ring = editableRing(acceptedRef.current);
      if (index === undefined || !ring) return;
      ring.splice(index + 1, 0, toPt(event.lngLat));
      onAcceptRef.current(closedPolygon(ring) as unknown as Record<string, unknown>);
    });
    for (const layer of [HANDLES_LAYER_ID, MIDPOINTS_LAYER_ID]) {
      map.on('mouseenter', layer, () => {
        if (drawEnabledRef.current && dragIndexRef.current === null) {
          map.getCanvas().style.cursor = layer === HANDLES_LAYER_ID ? 'grab' : 'copy';
        }
      });
      map.on('mouseleave', layer, () => {
        if (dragIndexRef.current === null) map.getCanvas().style.cursor = drawEnabledRef.current ? 'crosshair' : '';
      });
    }

    map.on('load', () => {
      styleReady = true;
      ensureBasemap(map, useBasemapStore.getState().choice);
      const c = COI_BOUNDARY_COLOR;

      // The attached boundary, whatever mode produced it.
      map.addSource(ACCEPTED_SOURCE_ID, { type: 'geojson', data: polygonCollection(acceptedRef.current) });
      map.addLayer({ id: ACCEPTED_FILL_LAYER_ID, type: 'fill', source: ACCEPTED_SOURCE_ID, paint: { 'fill-color': c, 'fill-opacity': 0.22 } });
      map.addLayer({ id: ACCEPTED_LINE_LAYER_ID, type: 'line', source: ACCEPTED_SOURCE_ID, paint: { 'line-color': c, 'line-width': 2.5 } });

      // The unaccepted AI-sketch candidate: lighter, dashed.
      map.addSource(SKETCH_SOURCE_ID, { type: 'geojson', data: polygonCollection(previewRef.current) });
      map.addLayer({ id: SKETCH_FILL_LAYER_ID, type: 'fill', source: SKETCH_SOURCE_ID, paint: { 'fill-color': c, 'fill-opacity': 0.1 } });
      map.addLayer({
        id: SKETCH_LINE_LAYER_ID,
        type: 'line',
        source: SKETCH_SOURCE_ID,
        paint: { 'line-color': c, 'line-width': 2, 'line-dasharray': [3, 2] },
      });

      // In-progress sketch.
      map.addSource(DRAW_SOURCE_ID, { type: 'geojson', data: emptyCollection() });
      map.addLayer({
        id: DRAW_FILL_LAYER_ID,
        type: 'fill',
        source: DRAW_SOURCE_ID,
        filter: ['==', '$type', 'Polygon'],
        paint: { 'fill-color': c, 'fill-opacity': 0.14 },
      });
      map.addLayer({
        id: DRAW_LINE_LAYER_ID,
        type: 'line',
        source: DRAW_SOURCE_ID,
        filter: ['all', ['==', '$type', 'LineString'], ['==', 'role', 'path']],
        paint: { 'line-color': c, 'line-width': 2.5 },
      });
      map.addLayer({
        id: DRAW_GUIDE_LAYER_ID,
        type: 'line',
        source: DRAW_SOURCE_ID,
        filter: ['all', ['==', '$type', 'LineString'], ['==', 'role', 'guide']],
        paint: { 'line-color': c, 'line-width': 1.5, 'line-dasharray': [2, 2], 'line-opacity': 0.8 },
      });
      map.addLayer({
        id: VERTICES_LAYER_ID,
        type: 'circle',
        source: DRAW_SOURCE_ID,
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': ['case', ['==', ['get', 'first'], true], 7, 4.5],
          'circle-color': ['case', ['==', ['get', 'first'], true], c, '#ffffff'],
          'circle-stroke-color': ['case', ['==', ['get', 'first'], true], '#ffffff', c],
          'circle-stroke-width': 2,
        },
      });

      // Edit handles for the attached polygon.
      map.addSource(HANDLES_SOURCE_ID, { type: 'geojson', data: emptyCollection() });
      map.addLayer({
        id: MIDPOINTS_LAYER_ID,
        type: 'circle',
        source: HANDLES_SOURCE_ID,
        filter: ['==', 'kind', 'mid'],
        paint: { 'circle-radius': 4, 'circle-color': c, 'circle-opacity': 0.45, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 },
      });
      map.addLayer({
        id: HANDLES_LAYER_ID,
        type: 'circle',
        source: HANDLES_SOURCE_ID,
        filter: ['==', 'kind', 'vertex'],
        paint: { 'circle-radius': 6, 'circle-color': '#ffffff', 'circle-stroke-color': c, 'circle-stroke-width': 2.5 },
      });

      refreshDrawLayers();
      refreshHandles();
      onMapReadyRef.current?.(map);
    });

    const handleError = (event: ErrorEvent): void => {
      setLoadError((prev) => prev ?? event.message);
    };
    map.on('error', handleError);

    return () => {
      resizeObserver?.disconnect();
      unsubscribeBasemap();
      map.off('error', handleError);
      map.remove();
      mapRef.current = null;
    };
    // The map is created once per tenant; drawing state lives in refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  const drawing = vertexCount > 0;
  const segBtn = (active: boolean): string =>
    `inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
      active ? 'bg-white text-ink shadow-sm ring-1 ring-stone-200' : 'text-stone-500 hover:text-ink'
    }`;

  return (
    <div className="relative h-full w-full">
      <div
        ref={containerRef}
        className="absolute inset-0"
        role="application"
        aria-label="Community map: street basemap and census block outlines for reference. In draw mode, click to add boundary points; click the first point, double-click, or press Enter to finish."
      />

      {/* Not drawing: a direct way in. */}
      {!drawEnabled && !accepted && !preview && onRequestDraw && (
        <div className="floating absolute bottom-3 left-3 z-20 max-w-[280px] p-3">
          <p className="text-sm font-semibold text-ink">Want to show it on the map?</p>
          <p className="mt-0.5 text-xs leading-snug text-stone-500">
            Optional — your written description is enough on its own.
          </p>
          <button type="button" onClick={onRequestDraw} className="btn-primary btn-sm mt-2.5">
            <PolygonIcon size={14} /> Draw the boundary
          </button>
        </div>
      )}

      {drawEnabled && (
        <div className="floating absolute bottom-3 left-3 z-20 w-[330px] max-w-[calc(100%-4.5rem)] p-2 sm:p-3">
          {!accepted && (
            <div className="mb-2 flex gap-0.5 rounded-lg bg-stone-100 p-0.5 sm:mb-2.5" role="radiogroup" aria-label="Drawing style">
              <button
                type="button"
                role="radio"
                aria-checked={drawStyle === 'click'}
                onClick={() => {
                  resetSketch();
                  setDrawStyle('click');
                }}
                className={segBtn(drawStyle === 'click')}
              >
                <PolygonIcon size={14} /> Click points
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={drawStyle === 'trace'}
                onClick={() => {
                  resetSketch();
                  setDrawStyle('trace');
                }}
                className={segBtn(drawStyle === 'trace')}
              >
                <PenIcon size={14} /> Trace
              </button>
            </div>
          )}

          {accepted ? (
            <>
              <p className="flex items-center justify-between gap-2 text-sm font-semibold text-ink">
                <span>Boundary attached</span>
                {area > 0 && <span className="text-xs font-medium tabular-nums text-stone-500">≈ {formatArea(area)}</span>}
              </p>
              <p className="mt-0.5 hidden text-[11px] leading-snug text-stone-500 sm:block">
                {acceptedRing
                  ? 'Drag a point to adjust it, click a faint midpoint to add one, right-click a point to remove it.'
                  : 'This boundary came from a sketch and can be redrawn, not edited point by point.'}
              </p>
              {crossing && (
                <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800" role="alert">
                  <AlertIcon size={14} className="mt-px shrink-0" />
                  The outline crosses itself. Drag a point to untangle it, or redraw.
                </p>
              )}
              <button type="button" onClick={clearDrawing} className="btn-secondary btn-sm mt-2.5">
                Redraw
              </button>
            </>
          ) : drawStyle === 'click' ? (
            <>
              <p className="text-sm font-semibold text-ink">
                {drawing ? `${vertexCount} point${vertexCount === 1 ? '' : 's'}` : 'Click around your community'}
              </p>
              {/* Phones: the hint is in the form under the map, so keep the map clear. */}
              <p className="mt-0.5 hidden text-[11px] leading-snug text-stone-500 sm:block">
                {nearFirst
                  ? 'Click to close the shape.'
                  : drawing && vertexCount >= 3
                    ? 'Click the first point, double-click, or press Enter to finish.'
                    : 'Drop a point at each corner of its edge.'}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 sm:mt-2.5">
                <button type="button" onClick={finishDrawing} disabled={vertexCount < 3} className="btn-primary btn-sm">
                  Finish <kbd className="kbd border-white/30 bg-white/15 text-white/80">↵</kbd>
                </button>
                <button type="button" onClick={undoPoint} disabled={!drawing} className="btn-secondary btn-sm" title="Remove the last point (Backspace)">
                  <UndoIcon size={14} /> Undo<span className="hidden sm:inline"> point</span>
                </button>
                <button type="button" onClick={clearDrawing} disabled={!drawing} className="btn-ghost btn-sm">
                  Clear
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-ink">Press and drag to trace</p>
              <p className="mt-0.5 hidden text-[11px] leading-snug text-stone-500 sm:block">
                Trace your community’s edge in one stroke — release to close the shape. The map
                doesn’t pan in trace mode; switch to “Click points” to move around.
              </p>
            </>
          )}
        </div>
      )}

      {!drawEnabled && preview && !accepted && (
        <div className="absolute right-3 top-14 z-20 rounded-lg bg-ink/85 px-2.5 py-1.5 text-xs font-medium text-white shadow">
          AI sketch preview — refine or attach it in the panel
        </div>
      )}
      {loadError && (
        <div
          className="absolute bottom-3 right-14 z-20 max-w-sm rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 shadow"
          role="alert"
        >
          Map preview unavailable: {loadError}. You can still submit with text only.
        </div>
      )}
    </div>
  );
}
